import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchBooks,
  saveBookThunk,
} from "../../redux/admin/book.thunk";
import { fetchCourseCategories } from "../../redux/admin/courseCategory.thunk";
import { fetchBranches } from "../../redux/admin/branch.thunk";
import { selectBooks, selectBookLoading } from "../../redux/admin/book.selector";

import toastr from "toastr";
import "toastr/build/toastr.min.css";

/* ─── Spinner component ─── */
const Spinner = ({ size = 16, color = "#ffffff", thickness = 2 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    style={{ animation: "bk-spin 0.72s linear infinite", flexShrink: 0 }}
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" stroke={color} strokeOpacity="0.22" strokeWidth={thickness} />
    <path d="M12 2a10 10 0 0 1 10 10" stroke={color} strokeWidth={thickness} strokeLinecap="round" />
  </svg>
);

/* ─── Skeleton shimmer ─── */
const Skeleton = ({ w = "100%", h = 14, r = 6, style: extra = {} }) => (
  <div
    style={{
      width: w, height: h, borderRadius: r,
      background: "linear-gradient(90deg,#f0f0f5 25%,#e8e8ed 50%,#f0f0f5 75%)",
      backgroundSize: "600px 100%",
      animation: "bk-shimmer 1.4s infinite linear",
      ...extra,
    }}
  />
);

const Books = () => {
  const dispatch = useDispatch();

  /* =====================================================
     REDUX SELECTORS
  ====================================================== */
  const books = useSelector(selectBooks);
  const loading = useSelector(selectBookLoading);
  
  const courseCategoryState = useSelector((state) => state.admin?.courseCategory || {});
  const branchState = useSelector((state) => state.admin?.branches || {});

  const globalCategories = courseCategoryState.courseCategories || courseCategoryState.categories || [];
  const globalBranches = branchState.branches || branchState.branchList || [];

  /* =====================================================
     SEARCH & FILTER STATES
  ====================================================== */
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState("");

  /* =====================================================
     MODAL & TABS STATE
  ====================================================== */
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState("details"); // details | inventory | files
  const [saving, setSaving] = useState(false);
  const [selectedBookId, setSelectedBookId] = useState(null);

  /* =====================================================
     INITIAL FORM STATE
  ====================================================== */
  const initialFormState = {
    course_categories_id: "",
    branch_id: "",
    title: "",
    author: "",
    published_year: new Date().getFullYear(),
    isbn: "",
    is_hard_copy: 1, // 1 = Yes, 0 = No
    rack_location: "",
    condition_status: "Good",
    book_cost: "",
    pdf_file: null,
    invoice_file: null,
    existing_pdf: "",
    existing_invoice: "",
  };

  const [formData, setFormData] = useState(initialFormState);

  /* =====================================================
     FETCH LIFECYCLE MANAGEMENT
  ====================================================== */
  useEffect(() => {
    dispatch(fetchCourseCategories());
    dispatch(fetchBranches({ page: 1, limit: 1000 }));
  }, [dispatch]);

  // Set default category filter once categories load
  useEffect(() => {
    if (globalCategories.length > 0 && !selectedCategoryFilter) {
      const firstId = String(globalCategories[0].id || globalCategories[0].course_categories_id);
      setSelectedCategoryFilter(firstId);
    }
  }, [globalCategories, selectedCategoryFilter]);

  // Fetch books when category filter changes
  useEffect(() => {
    if (selectedCategoryFilter) {
      dispatch(fetchBooks(selectedCategoryFilter));
    }
  }, [dispatch, selectedCategoryFilter]);

  /* =====================================================
     HANDLERS & HELPERS
  ====================================================== */
  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === "file") {
      setFormData((prev) => ({ ...prev, [name]: files[0] }));
    } else if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked ? 1 : 0 }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const openAddModal = () => {
    setIsEditMode(false);
    setSelectedBookId(null);
    setFormData({
      ...initialFormState,
      course_categories_id: selectedCategoryFilter || (globalCategories[0]?.id || ""),
    });
    setActiveTab("details");
    setShowModal(true);
  };

  const openEditModal = (book) => {
    setIsEditMode(true);
    setSelectedBookId(book.book_id);
    setFormData({
      course_categories_id: String(book.course_categories_id || ""),
      branch_id: String(book.branch_id || ""),
      title: book.title || "",
      author: book.author || "",
      published_year: book.published_year || new Date().getFullYear(),
      isbn: book.isbn || "",
      is_hard_copy: book.is_hard_copy ?? 1,
      rack_location: book.rack_location || "",
      condition_status: book.condition_status || "Good",
      book_cost: book.book_cost || "",
      pdf_file: null,
      invoice_file: null,
      existing_pdf: book.pdf_file || "",
      existing_invoice: book.invoice_file || "",
    });
    setActiveTab("details");
    setShowModal(true);
  };

  /* =====================================================
     FORM SUBMIT
  ====================================================== */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toastr.warning("Book title is required.");
      setActiveTab("details");
      return;
    }
    if (!formData.course_categories_id) {
      toastr.warning("Course category selection is required.");
      setActiveTab("details");
      return;
    }

    setSaving(true);
    try {
      const data = new FormData();
      if (selectedBookId) {
        data.append("book_id", selectedBookId);
      }

      Object.keys(formData).forEach((key) => {
        if (key === "existing_pdf" || key === "existing_invoice") return; // Skip preview tracking keys
        if (formData[key] !== null && formData[key] !== "") {
          data.append(key, formData[key]);
        }
      });

      const result = await dispatch(saveBookThunk(data)).unwrap();
      toastr.success(isEditMode ? "Book record updated successfully." : "New book registered successfully.");
      setShowModal(false);
      dispatch(fetchBooks(selectedCategoryFilter));
    } catch (err) {
      toastr.error(err || "Failed to save book record.");
    } finally {
      setSaving(false);
    }
  };

  // Filter books locally by search term
  const filteredBooks = books.filter((b) =>
    (b.title || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (b.author || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (b.isbn || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getCategoryName = (catId) => {
    const match = globalCategories.find((c) => String(c.id || c.course_categories_id) === String(catId));
    return match ? (match.name || match.category_name) : "General Category";
  };

  const getBranchName = (branchId) => {
    const match = globalBranches.find((b) => String(b.id) === String(branchId));
    return match ? match.branch_name : "Main Branch";
  };

  // Base URL for storage files (adjust domain/storage base path if needed)
  const getStorageUrl = (path) => {
    if (!path) return "#";
    if (path.startsWith("http")) return path;
    return `https://aadhavacademy.com/${path.replace(/^\//, "")}`;
  };

  /* ─────────────────────────────────────────────
     RENDER
  ───────────────────────────────────────────── */
  return (
    <div className="bk-page">
      <div className="bk-wrap">

        {/* ── PAGE HEADER ── */}
        <div className="bk-header">
          <div>
            <p className="bk-eyebrow">Library &amp; Assets · Admin Console</p>
            <h1 className="bk-title">Books Inventory</h1>
          </div>
          <button type="button" className="bk-btn-primary" onClick={openAddModal}>
            <i className="fas fa-plus"></i> Register Book
          </button>
        </div>

        {/* ── KPI STRIP ── */}
        <div className="bk-kpi-grid">
          {[
            { label: "Total catalog", value: books.length },
            { label: "Hard copies", value: books.filter((b) => b.is_hard_copy == 1).length },
            { label: "Digital ebooks", value: books.filter((b) => b.pdf_file).length },
          ].map(({ label, value }) => (
            <div key={label} className="bk-kpi">
              <span className="bk-kpi-label">{label}</span>
              <span className="bk-kpi-val">{loading ? <Skeleton w={40} h={22} r={5} /> : value}</span>
            </div>
          ))}
        </div>

        {/* ── TOOLBAR ── */}
        <div className="bk-toolbar">
          <div className="bk-search">
            <i className="fas fa-search bk-search-icon"></i>
            <input
              type="text"
              placeholder="Search by title, author, or ISBN…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="bk-filter-select-wrap">
            <select
              className="bk-field"
              value={selectedCategoryFilter}
              onChange={(e) => setSelectedCategoryFilter(e.target.value)}
            >
              <option value="">All Categories</option>
              {globalCategories.map((cat) => {
                const catId = cat.id || cat.course_categories_id;
                const catName = cat.name || cat.category_name;
                return (
                  <option key={catId} value={catId}>{catName}</option>
                );
              })}
            </select>
          </div>
        </div>

        {/* ── DATA TABLE ── */}
        <div className="bk-card">
          <div className="table-responsive">
            <table className="bk-table">
              <thead>
                <tr>
                  <th style={{ width: "32%" }}>Book title &amp; Author</th>
                  <th style={{ width: "16%" }}>Category</th>
                  <th style={{ width: "12%" }}>Format</th>
                  <th style={{ width: "14%" }}>Cost / Price</th>
                  <th style={{ width: "16%" }}>Location / Rack</th>
                  <th className="text-end" style={{ width: "10%" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="bk-row">
                      <td>
                        <div className="bk-name-cell">
                          <Skeleton w={34} h={34} r={8} style={{ flexShrink: 0 }} />
                          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 5 }}>
                            <Skeleton w="70%" h={12} />
                            <Skeleton w="45%" h={10} />
                          </div>
                        </div>
                      </td>
                      <td><Skeleton w={90} h={12} /></td>
                      <td><Skeleton w={60} h={22} r={20} /></td>
                      <td><Skeleton w={60} h={12} /></td>
                      <td><Skeleton w={90} h={12} /></td>
                      <td style={{ textAlign: "right" }}><Skeleton w={52} h={30} r={7} style={{ marginLeft: "auto" }} /></td>
                    </tr>
                  ))
                ) : filteredBooks.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="bk-empty-state">
                      <div className="bk-empty-icon-wrap">
                        <i className="fas fa-book-open"></i>
                      </div>
                      <p className="bk-empty-title">No books found</p>
                      <span className="bk-empty-desc">Select a different category or register a new book asset.</span>
                    </td>
                  </tr>
                ) : (
                  filteredBooks.map((book) => (
                    <tr key={book.book_id} className="bk-row">
                      <td>
                        <div className="bk-name-cell">
                          <div className="bk-avatar"><i className="fas fa-book"></i></div>
                          <div>
                            <div className="bk-name">{book.title}</div>
                            <div className="bk-meta">{book.author || "Unknown author"} {book.published_year ? `(${book.published_year})` : ""}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="bk-pill pill-blue">{getCategoryName(book.course_categories_id)}</span>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <span className={`bk-pill ${book.is_hard_copy == 1 ? "pill-green" : "pill-amber"}`}>
                            {book.is_hard_copy == 1 ? "Hard Copy" : "Digital Only"}
                          </span>
                          {book.pdf_file && (
                            <a
                              href={getStorageUrl(book.pdf_file)}
                              target="_blank"
                              rel="noreferrer"
                              className="bk-file-badge-icon"
                              title="Download / View eBook PDF"
                            >
                              <i className="fas fa-file-pdf"></i>
                            </a>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="bk-main-text">{book.book_cost ? `₹${parseFloat(book.book_cost).toFixed(2)}` : "—"}</div>
                      </td>
                      <td>
                        <div className="bk-main-text">{book.rack_location || "Main Storage"}</div>
                        <div className="bk-meta">{getBranchName(book.branch_id)}</div>
                      </td>
                      <td className="text-end">
                        <button type="button" className="bk-btn-outline" onClick={() => openEditModal(book)}>
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* ── FOOTER ── */}
          <div className="bk-footer">
            <span className="bk-count">Showing {filteredBooks.length} items</span>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════
          MODAL
      ═══════════════════════════════════════ */}
      {showModal && (
        <div
          className="bk-modal-overlay"
          onClick={(e) => e.target === e.currentTarget && !saving && setShowModal(false)}
        >
          <div className="bk-modal">

            {/* Sidebar */}
            <div className="bk-modal-side">
              <div className="bk-modal-side-header">
                <p className="bk-modal-side-title">Book profile</p>
                <p className="bk-modal-side-sub">Asset configuration</p>
              </div>
              <nav className="bk-modal-nav">
                {[
                  { key: "details", icon: "fas fa-info-circle", label: "Book details" },
                  { key: "inventory", icon: "fas fa-warehouse", label: "Asset & Location" },
                  { key: "files", icon: "fas fa-file-pdf", label: "Documents & Files" },
                ].map(({ key, icon, label }) => (
                  <button
                    key={key}
                    type="button"
                    className={`bk-nav-item${activeTab === key ? " active" : ""}`}
                    onClick={() => setActiveTab(key)}
                    disabled={saving}
                  >
                    <i className={icon}></i>
                    <span>{label}</span>
                  </button>
                ))}
              </nav>
            </div>

            {/* Main panel */}
            <form onSubmit={handleSubmit} className="bk-modal-main">
              <div className="bk-modal-header">
                <div>
                  <h2 className="bk-modal-title">{isEditMode ? "Edit book asset" : "Register new book"}</h2>
                  <p className="bk-modal-sub">
                    {isEditMode ? "Update asset specifications below." : "Enter the book information and upload attachments."}
                  </p>
                </div>
                <button type="button" className="btn-close shadow-none" onClick={() => setShowModal(false)} aria-label="Close" disabled={saving}></button>
              </div>

              <div className="bk-modal-body">
                <div className="row g-3">

                  {/* ── DETAILS TAB ── */}
                  {activeTab === "details" && (
                    <>
                      <div className="col-12">
                        <label className="bk-label">Course category *</label>
                        <select
                          name="course_categories_id"
                          className="bk-field"
                          value={formData.course_categories_id}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Select course category</option>
                          {globalCategories.map((cat) => {
                            const catId = cat.id || cat.course_categories_id;
                            const catName = cat.name || cat.category_name;
                            return (
                              <option key={catId} value={catId}>{catName}</option>
                            );
                          })}
                        </select>
                      </div>
                      <div className="col-12">
                        <label className="bk-label">Book title *</label>
                        <input
                          type="text"
                          name="title"
                          className="bk-field"
                          placeholder="e.g. Boost Your Chess 1: The Fundamentals"
                          value={formData.title}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="bk-label">Author</label>
                        <input
                          type="text"
                          name="author"
                          className="bk-field"
                          placeholder="e.g. Artur Yusupov"
                          value={formData.author}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="bk-label">Published year</label>
                        <input
                          type="number"
                          name="published_year"
                          className="bk-field"
                          placeholder="e.g. 2010"
                          value={formData.published_year}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="col-12">
                        <label className="bk-label">ISBN reference</label>
                        <input
                          type="text"
                          name="isbn"
                          className="bk-field"
                          placeholder="e.g. 978-1-906552-40-4"
                          value={formData.isbn}
                          onChange={handleChange}
                        />
                      </div>
                    </>
                  )}

                  {/* ── INVENTORY & LOCATION TAB ── */}
                  {activeTab === "inventory" && (
                    <>
                      <div className="col-12">
                        <div className="bk-status-toggle-row">
                          <div>
                            <label className="bk-label" style={{ marginBottom: 2 }}>Physical hard copy</label>
                            <span className="bk-status-toggle-sub">Track physical stock across academy branches.</span>
                          </div>
                          <div className="form-check form-switch m-0">
                            <input
                              className="form-check-input bk-switch"
                              type="checkbox"
                              name="is_hard_copy"
                              checked={formData.is_hard_copy === 1}
                              onChange={handleChange}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <label className="bk-label">Branch location</label>
                        <select
                          name="branch_id"
                          className="bk-field"
                          value={formData.branch_id}
                          onChange={handleChange}
                        >
                          <option value="">Select branch</option>
                          {globalBranches.map((b) => (
                            <option key={b.id} value={b.id}>{b.branch_name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="bk-label">Condition status</label>
                        <select
                          name="condition_status"
                          className="bk-field"
                          value={formData.condition_status}
                          onChange={handleChange}
                        >
                          <option value="New">New</option>
                          <option value="Good">Good</option>
                          <option value="Fair">Fair</option>
                          <option value="Damaged">Damaged</option>
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="bk-label">Purchase cost (₹)</label>
                        <input
                          type="number"
                          step="0.01"
                          name="book_cost"
                          className="bk-field"
                          placeholder="0.00"
                          value={formData.book_cost}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="bk-label">Rack / Shelf location</label>
                        <input
                          type="text"
                          name="rack_location"
                          className="bk-field"
                          placeholder="e.g. Rack 2, Shelf B"
                          value={formData.rack_location}
                          onChange={handleChange}
                        />
                      </div>
                    </>
                  )}

                  {/* ── DOCUMENTS & FILES TAB ── */}
                  {activeTab === "files" && (
                    <>
                      <div className="col-12">
                        <div className="bk-section-note">
                          Upload digital assets such as the eBook PDF resource or vendor purchase invoice receipts for auditing.
                        </div>
                      </div>

                      {/* PDF FILE */}
                      <div className="col-12">
                        <label className="bk-label">Digital eBook (PDF file)</label>
                        {formData.existing_pdf && !formData.pdf_file && (
                          <div className="bk-file-existing-card">
                            <div className="d-flex align-items-center gap-2">
                              <i className="fas fa-file-pdf text-danger fs-5"></i>
                              <span className="text-dark fw-500 fs-13 text-truncate" style={{ maxWidth: "260px" }}>
                                {formData.existing_pdf.split("/").pop()}
                              </span>
                            </div>
                            <a
                              href={getStorageUrl(formData.existing_pdf)}
                              target="_blank"
                              rel="noreferrer"
                              className="bk-btn-view-file"
                            >
                              <i className="fas fa-external-link-alt me-1"></i> View / Download
                            </a>
                          </div>
                        )}
                        <input
                          type="file"
                          name="pdf_file"
                          accept=".pdf"
                          className="bk-field mt-2"
                          onChange={handleChange}
                          style={{ padding: "7px 12px" }}
                        />
                        <span className="bk-status-toggle-sub" style={{ marginTop: 4, display: "block" }}>
                          {formData.pdf_file ? `New file selected: ${formData.pdf_file.name}` : formData.existing_pdf ? "Choose a new PDF file above to replace the existing one." : "Upload study PDF."}
                        </span>
                      </div>

                      {/* INVOICE FILE */}
                      <div className="col-12" style={{ marginTop: 8 }}>
                        <label className="bk-label">Purchase invoice receipt</label>
                        {formData.existing_invoice && !formData.invoice_file && (
                          <div className="bk-file-existing-card">
                            <div className="d-flex align-items-center gap-2">
                              <i className="fas fa-file-invoice text-primary fs-5"></i>
                              <span className="text-dark fw-500 fs-13 text-truncate" style={{ maxWidth: "260px" }}>
                                {formData.existing_invoice.split("/").pop()}
                              </span>
                            </div>
                            <a
                              href={getStorageUrl(formData.existing_invoice)}
                              target="_blank"
                              rel="noreferrer"
                              className="bk-btn-view-file"
                            >
                              <i className="fas fa-external-link-alt me-1"></i> View / Download
                            </a>
                          </div>
                        )}
                        <input
                          type="file"
                          name="invoice_file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          className="bk-field mt-2"
                          onChange={handleChange}
                          style={{ padding: "7px 12px" }}
                        />
                        <span className="bk-status-toggle-sub" style={{ marginTop: 4, display: "block" }}>
                          {formData.invoice_file ? `New file selected: ${formData.invoice_file.name}` : formData.existing_invoice ? "Choose a new bill or receipt above to replace the existing one." : "Upload bill or receipt image/PDF."}
                        </span>
                      </div>
                    </>
                  )}

                </div>
              </div>

              {/* ── MODAL FOOTER ── */}
              <div className="bk-modal-footer">
                <button type="button" className="bk-btn-cancel" onClick={() => setShowModal(false)} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="bk-btn-primary bk-btn-submit" disabled={saving}>
                  {saving ? (
                    <>
                      <Spinner size={15} color="#ffffff" thickness={2} />
                      {isEditMode ? "Saving changes…" : "Registering…"}
                    </>
                  ) : (
                    isEditMode ? "Save changes" : "Register book"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── STYLES ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,400;0,14..32,500;0,14..32,600;1,14..32,400&display=swap');

        @keyframes bk-spin { to { transform: rotate(360deg); } }
        @keyframes bk-shimmer {
          0%   { background-position: -600px 0; }
          100% { background-position:  600px 0; }
        }
        @keyframes bk-modal-in {
          from { opacity: 0; transform: scale(0.97) translateY(10px); }
          to   { opacity: 1; transform: scale(1)    translateY(0);     }
        }
        @keyframes bk-overlay-in { from { opacity: 0; } to { opacity: 1; } }

        /* ── BASE ── */
        .bk-page {
          background: #f5f5f7;
          min-height: 100vh;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          color: #1d1d1f;
          font-size: 14px;
        }
        .bk-wrap { max-width: 1200px; margin: 0 auto; padding: 2rem 1.5rem; }

        /* ── PAGE HEADER ── */
        .bk-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; flex-wrap: wrap; margin-bottom: 1.5rem; }
        .bk-eyebrow { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.07em; color: #aeaeb2; margin: 0 0 4px; }
        .bk-title { font-size: 1.35rem; font-weight: 600; color: #1d1d1f; letter-spacing: -0.022em; margin: 0; }

        /* ── KPI STRIP ── */
        .bk-kpi-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 1.25rem; }
        @media (max-width: 640px) { .bk-kpi-grid { grid-template-columns: repeat(2, 1fr); } }
        .bk-kpi { background: #fff; border: 1px solid #d2d2d7; border-radius: 12px; padding: 14px 18px; display: flex; flex-direction: column; gap: 5px; }
        .bk-kpi-label { font-size: 11px; color: #6e6e73; font-weight: 500; text-transform: uppercase; letter-spacing: 0.05em; }
        .bk-kpi-val { font-size: 1.5rem; font-weight: 600; color: #1d1d1f; letter-spacing: -0.025em; font-variant-numeric: tabular-nums; min-height: 1.8rem; display: flex; align-items: center; }

        /* ── TOOLBAR ── */
        .bk-toolbar { display: flex; align-items: center; gap: 10px; margin-bottom: 1rem; flex-wrap: wrap; }
        .bk-search { flex: 1; min-width: 200px; position: relative; }
        .bk-search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #aeaeb2; font-size: 12px; }
        .bk-search input {
          width: 100%; background: #fff; border: 1px solid #d2d2d7; border-radius: 10px;
          padding: 9px 12px 9px 34px; font-size: 13px; color: #1d1d1f; outline: none;
          transition: border-color 0.15s, box-shadow 0.15s; font-family: inherit;
        }
        .bk-search input:focus { border-color: #0071e3; box-shadow: 0 0 0 3px rgba(0,113,227,0.15); }
        .bk-search input::placeholder { color: #aeaeb2; }

        .bk-filter-select-wrap { min-width: 220px; }

        /* ── PRIMARY BUTTON ── */
        .bk-btn-primary {
          background: #0071e3; color: #fff; border: none; border-radius: 10px; padding: 9px 18px;
          font-size: 13px; font-weight: 500; cursor: pointer; display: inline-flex; align-items: center; gap: 7px;
          transition: background 0.15s, opacity 0.15s; white-space: nowrap; font-family: inherit;
          min-width: 140px; justify-content: center;
        }
        .bk-btn-primary:hover:not(:disabled) { background: #0077ed; }
        .bk-btn-primary:disabled { opacity: 0.65; cursor: not-allowed; }
        .bk-btn-submit { padding: 9px 22px; min-width: 158px; }

        /* ── TABLE CARD ── */
        .bk-card { background: #fff; border: 1px solid #d2d2d7; border-radius: 14px; overflow: hidden; }
        .bk-table { width: 100%; border-collapse: collapse; }
        .bk-table thead th {
          background: #f5f5f7; font-size: 10.5px; font-weight: 600; color: #6e6e73;
          text-transform: uppercase; letter-spacing: 0.07em; padding: 11px 18px;
          border-bottom: 1px solid #e8e8ed; white-space: nowrap;
        }
        .bk-table tbody tr { border-bottom: 1px solid #f5f5f7; transition: background 0.1s; }
        .bk-table tbody tr:last-child { border-bottom: none; }
        .bk-row:hover { background: #fafafa; }
        .bk-table td { padding: 13px 18px; vertical-align: middle; }

        .bk-name-cell { display: flex; align-items: center; gap: 10px; }
        .bk-avatar {
          width: 34px; height: 34px; border-radius: 8px; background: #f5f5f7; border: 1px solid #e8e8ed;
          color: #6e6e73; font-size: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .bk-name { font-size: 13px; font-weight: 500; color: #1d1d1f; }
        .bk-meta { font-size: 11.5px; color: #6e6e73; margin-top: 2px; }

        .bk-file-badge-icon {
          width: 26px; height: 26px; border-radius: 6px; background: #fee2e2; color: #ef4444;
          display: inline-flex; align-items: center; justify-content: center; font-size: 11px;
          transition: transform 0.12s; text-decoration: none;
        }
        .bk-file-badge-icon:hover { transform: scale(1.08); background: #fecaca; }

        .bk-file-existing-card {
          display: flex; align-items: center; justify-content: space-between;
          background: #f5f5f7; border: 1px solid #e8e8ed; border-radius: 8px; padding: 8px 12px; margin-bottom: 4px;
        }
        .bk-btn-view-file {
          font-size: 11.5px; font-weight: 500; color: #0071e3; background: #fff; border: 1px solid #d2d2d7;
          border-radius: 6px; padding: 4px 10px; text-decoration: none; white-space: nowrap; transition: background 0.12s;
        }
        .bk-btn-view-file:hover { background: #e8f0fe; color: #0058b0; }

        /* ── PILLS ── */
        .bk-pill { display: inline-block; padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 500; white-space: nowrap; }
        .pill-amber { background: #fef3c7; color: #92400e; border: 1px solid #fde68a; }
        .pill-green { background: #dcfce7; color: #166534; border: 1px solid #bbf7d0; }
        .pill-blue  { background: #dbeafe; color: #1e40af; border: 1px solid #bfdbfe; }

        .bk-main-text { font-size: 12.5px; font-weight: 500; color: #1d1d1f; font-variant-numeric: tabular-nums; }

        /* ── OUTLINE BUTTON ── */
        .bk-btn-outline {
          border: 1px solid #d2d2d7; background: #fff; border-radius: 8px; padding: 6px 14px;
          font-size: 12px; font-weight: 500; color: #3c3c43; cursor: pointer; transition: all 0.12s; font-family: inherit;
        }
        .bk-btn-outline:hover { border-color: #aeaeb2; background: #f5f5f7; color: #1d1d1f; }

        /* ── EMPTY STATE ── */
        .bk-empty-state { text-align: center; padding: 3rem 1rem; }
        .bk-empty-icon-wrap {
          width: 52px; height: 52px; background: #f5f5f7; border-radius: 14px;
          display: inline-flex; align-items: center; justify-content: center; font-size: 1.4rem; color: #aeaeb2; margin-bottom: 12px;
        }
        .bk-empty-title { font-size: 14px; font-weight: 500; color: #3c3c43; margin: 0 0 4px; }
        .bk-empty-desc { font-size: 12px; color: #aeaeb2; }

        /* ── FOOTER ── */
        .bk-footer { display: flex; align-items: center; justify-content: space-between; padding: 12px 18px; border-top: 1px solid #f5f5f7; }
        .bk-count { font-size: 12px; color: #6e6e73; font-weight: 500; }

        /* ── MODAL ── */
        .bk-modal-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,0.36); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
          display: flex; align-items: center; justify-content: center; z-index: 9999; padding: 16px; animation: bk-overlay-in 0.2s ease;
        }
        .bk-modal {
          width: 100%; max-width: 900px; height: 590px; background: #fff; border-radius: 18px; border: 1px solid #d2d2d7;
          box-shadow: 0 32px 60px -12px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.06);
          display: flex; overflow: hidden; animation: bk-modal-in 0.24s cubic-bezier(0.34,1.1,0.64,1);
        }

        /* Sidebar */
        .bk-modal-side { width: 210px; background: #f5f5f7; border-right: 1px solid #e8e8ed; display: flex; flex-direction: column; flex-shrink: 0; }
        .bk-modal-side-header { padding: 18px 16px; border-bottom: 1px solid #e8e8ed; }
        .bk-modal-side-title { font-size: 13px; font-weight: 600; color: #1d1d1f; margin: 0; }
        .bk-modal-side-sub { font-size: 11px; color: #aeaeb2; margin: 3px 0 0; }
        .bk-modal-nav { padding: 8px; display: flex; flex-direction: column; gap: 2px; }
        .bk-nav-item {
          display: flex; align-items: center; gap: 9px; padding: 9px 12px; border-radius: 9px; border: none; background: transparent;
          font-size: 13px; color: #6e6e73; font-weight: 400; cursor: pointer; text-align: left; width: 100%;
          transition: all 0.12s; position: relative; font-family: inherit;
        }
        .bk-nav-item i { font-size: 12.5px; width: 14px; text-align: center; flex-shrink: 0; }
        .bk-nav-item span { flex: 1; }
        .bk-nav-item:hover:not(.active):not(:disabled) { background: #ebebf0; color: #3c3c43; }
        .bk-nav-item.active { background: #fff; color: #0071e3; font-weight: 500; border: 1px solid #e8e8ed; }
        .bk-nav-item:disabled { opacity: 0.5; cursor: not-allowed; }

        /* Main panel */
        .bk-modal-main { flex: 1; display: flex; flex-direction: column; min-width: 0; }
        .bk-modal-header { padding: 18px 26px; border-bottom: 1px solid #f5f5f7; display: flex; align-items: flex-start; justify-content: space-between; }
        .bk-modal-title { font-size: 15px; font-weight: 600; color: #1d1d1f; margin: 0; letter-spacing: -0.015em; }
        .bk-modal-sub { font-size: 12px; color: #6e6e73; margin: 3px 0 0; }
        .bk-modal-body { flex: 1; overflow-y: auto; padding: 22px 26px; }
        .bk-modal-footer { padding: 14px 26px; border-top: 1px solid #f5f5f7; display: flex; justify-content: flex-end; align-items: center; gap: 10px; background: #f5f5f7; }

        /* ── Form fields ── */
        .bk-label { display: block; font-size: 11px; font-weight: 600; color: #6e6e73; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 5px; }
        .bk-field {
          width: 100%; border: 1px solid #d2d2d7; border-radius: 9px; padding: 9px 12px; font-size: 13px; color: #1d1d1f;
          background: #fff; outline: none; transition: border-color 0.15s, box-shadow 0.15s; font-family: inherit;
        }
        .bk-field:focus { border-color: #0071e3; box-shadow: 0 0 0 3px rgba(0,113,227,0.15); }
        .bk-field::placeholder { color: #aeaeb2; }
        select.bk-field { cursor: pointer; }

        .bk-section-note {
          background: #f5f5f7; border: 1px solid #e8e8ed; border-radius: 9px; padding: 10px 14px;
          font-size: 12.5px; color: #6e6e73; line-height: 1.6;
        }

        .bk-status-toggle-row {
          display: flex; align-items: center; justify-content: space-between; gap: 12px;
          background: #f5f5f7; border: 1px solid #e8e8ed; border-radius: 9px; padding: 12px 16px;
        }
        .bk-status-toggle-sub { font-size: 11.5px; color: #aeaeb2; }

        /* ── CANCEL BUTTON ── */
        .bk-btn-cancel {
          border: 1px solid #d2d2d7; background: #fff; border-radius: 9px; padding: 9px 18px;
          font-size: 13px; font-weight: 500; color: #6e6e73; cursor: pointer; transition: all 0.12s; font-family: inherit;
        }
        .bk-btn-cancel:hover:not(:disabled) { background: #f5f5f7; color: #3c3c43; }
        .bk-btn-cancel:disabled { opacity: 0.5; cursor: not-allowed; }

        /* ── MODAL BODY SCROLLBAR ── */
        .bk-modal-body::-webkit-scrollbar { width: 5px; }
        .bk-modal-body::-webkit-scrollbar-track { background: transparent; }
        .bk-modal-body::-webkit-scrollbar-thumb { background: #d2d2d7; border-radius: 3px; }

        /* ── RESPONSIVE ── */
        @media (max-width: 768px) {
          .bk-modal { flex-direction: column; height: auto; max-height: 90vh; }
          .bk-modal-side { width: 100%; border-right: none; border-bottom: 1px solid #e8e8ed; }
          .bk-modal-side-header { display: none; }
          .bk-modal-nav { flex-direction: row; overflow-x: auto; padding: 6px; scrollbar-width: none; }
          .bk-modal-nav::-webkit-scrollbar { display: none; }
          .bk-nav-item { white-space: nowrap; width: auto; font-size: 12px; padding: 7px 12px; }
          .bk-modal-body { padding: 16px; }
          .bk-modal-footer { padding: 12px 16px; }
          .bk-modal-header { padding: 14px 16px; }
        }
        @media (max-width: 480px) {
          .bk-wrap { padding: 1rem; }
          .bk-header { flex-direction: column; }
          .bk-btn-primary { width: 100%; justify-content: center; }
          .bk-toolbar { flex-direction: column; }
          .bk-filter-select-wrap { width: 100%; }
          .bk-modal-footer { flex-wrap: wrap; }
          .bk-btn-submit { width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default Books;